<template>
  <div class="protocol-media-gallery">
    <b-card>
      <template #header>
        <b-row class="align-items-center">
          <b-col>
            <h5 class="mb-0">
              <i class="fas fa-images mr-2"></i>
              Protocol Media
              <b-badge v-if="total > 0" variant="primary" class="ml-2">
                {{ total.toLocaleString() }} images
              </b-badge>
            </h5>
          </b-col>
          <b-col cols="auto">
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="isLoading"
              @click="fetchPage"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <!-- Filters -->
      <b-row class="mb-3">
        <b-col md="3">
          <b-form-group label="Clinic ID" label-size="sm" class="mb-2">
            <b-form-input
              v-model="filters.clinicId"
              type="number"
              size="sm"
              placeholder="All clinics"
              @keyup.enter="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="From" label-size="sm" class="mb-2">
            <b-form-input v-model="filters.from" type="date" size="sm" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="To" label-size="sm" class="mb-2">
            <b-form-input v-model="filters.to" type="date" size="sm" />
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end">
          <b-form-group class="mb-2 w-100">
            <b-button
              size="sm"
              variant="primary"
              class="mr-2"
              :disabled="isLoading"
              @click="applyFilters"
            >
              <i class="fas fa-search mr-1"></i> Search
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="clearFilters">
              Clear
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Loading -->
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="mt-2 text-muted">Loading protocol media...</p>
      </div>

      <!-- Error -->
      <b-alert v-else-if="errorMessage" show variant="danger">
        {{ errorMessage }}
      </b-alert>

      <!-- Empty -->
      <div v-else-if="items.length === 0" class="text-center py-5">
        <i class="fas fa-images fa-3x text-muted mb-3"></i>
        <h5 class="text-muted">No protocol media found</h5>
        <p class="text-muted mb-0">Try widening the date range or clearing filters.</p>
      </div>

      <!-- Grid -->
      <div v-else>
        <div class="media-grid">
          <div
            v-for="(item, index) in items"
            :key="item.id"
            class="media-tile"
            @click="openLightbox(index)"
          >
            <div class="media-thumb">
              <!--
                Always a thumbnail, never the original: originals average
                several MB each, so a grid of them would pull hundreds of MB
                per screen. The src is a presigned S3 URL resolved through the
                API, because an <img> cannot send the Authorization header the
                API requires.
              -->
              <img
                v-if="thumbUrl(item) && !failedIds.includes(item.id)"
                :src="thumbUrl(item)"
                :alt="captionFor(item)"
                loading="lazy"
                decoding="async"
                @error="markFailed(item.id)"
              />
              <div v-else-if="isResolving(item)" class="media-thumb-pending">
                <b-spinner small variant="secondary"></b-spinner>
              </div>
              <div v-else class="media-thumb-fallback">
                <i class="fas fa-file-image fa-2x text-muted"></i>
                <div class="small text-muted mt-1">No preview</div>
              </div>
            </div>

            <div class="media-caption">
              <div class="text-truncate font-weight-bold" :title="item.clinicName">
                {{ item.clinicName || "—" }}
              </div>
              <div class="text-truncate small text-muted" :title="patientLabel(item)">
                {{ patientLabel(item) }}
              </div>
              <div class="text-truncate small text-muted">
                {{ viewLabel(item) }}
              </div>
              <div class="small text-muted">{{ dateLabel(item) }}</div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <b-row class="mt-3 align-items-center">
          <b-col>
            <small class="text-muted">
              Showing {{ offset + 1 }} - {{ Math.min(offset + items.length, total) }}
              of {{ total.toLocaleString() }}
            </small>
          </b-col>
          <b-col cols="auto">
            <b-pagination
              v-model="currentPage"
              :total-rows="total"
              :per-page="perPage"
              size="sm"
              class="mb-0"
              @change="onPageChange"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>

    <Lightbox :index="lightboxIndex" :items="lightboxItems" @close="closeLightbox" />
  </div>
</template>

<script>
import VueLightbox from "vue-lightbox-lite";
import "vue-lightbox-lite/dist/index.css";
import moment from "moment";

import SuperadminService from "@/services/SuperadminService";

export default {
  name: "ProtocolMediaGallery",
  components: { Lightbox: VueLightbox },
  data() {
    return {
      items: [],
      total: 0,
      perPage: 24,
      currentPage: 1,
      isLoading: false,
      errorMessage: "",

      // Thumbnails resolved after the listing, keyed by media file id. The
      // listing carries a URL only for files that already had a thumbnail;
      // the rest are generated on demand and land here.
      resolvedThumbs: {},
      resolvingIds: [],

      // Tiles whose thumbnail cannot be produced (PDF, video, 3D model) or
      // whose image failed to load. Showing an icon is the expected outcome
      // for those, not an error worth surfacing.
      failedIds: [],

      filters: {
        clinicId: "",
        from: "",
        to: ""
      },

      lightboxIndex: null,
      lightboxItems: []
    };
  },
  computed: {
    offset() {
      return (this.currentPage - 1) * this.perPage;
    }
  },
  created() {
    this.fetchPage();
  },
  methods: {
    async fetchPage() {
      this.isLoading = true;
      this.errorMessage = "";

      try {
        const params = { limit: this.perPage, offset: this.offset };
        if (this.filters.clinicId) params.clinicId = this.filters.clinicId;
        if (this.filters.from) params.from = `${this.filters.from} 00:00:00`;
        if (this.filters.to) params.to = `${this.filters.to} 23:59:59`;

        const res = await SuperadminService.searchProtocolMedia(params);

        this.items = res.data.data || [];
        this.total = res.data.pagination ? res.data.pagination.total : 0;
        this.failedIds = [];
        this.resolvedThumbs = {};
        this.resolvingIds = [];

        this.resolveMissingThumbnails();
      } catch (error) {
        this.items = [];
        this.total = 0;
        this.errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          "Failed to load protocol media";
      } finally {
        this.isLoading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchPage();
    },
    clearFilters() {
      this.filters = { clinicId: "", from: "", to: "" };
      this.applyFilters();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.$nextTick(this.fetchPage);
    },
    /**
     * Generates the thumbnails this page is missing, a chunk at a time so
     * tiles fill in progressively instead of the whole grid waiting on the
     * slowest image. Generating one decodes a multi-megabyte original, so the
     * server caps how many it will take at once.
     */
    async resolveMissingThumbnails() {
      const pending = this.items
        .filter(item => !item.thumbnailURL)
        .map(item => item.id);

      if (pending.length === 0) return;

      this.resolvingIds = [...pending];

      const CHUNK = 6;
      for (let i = 0; i < pending.length; i += CHUNK) {
        const chunk = pending.slice(i, i + CHUNK);

        try {
          const res = await SuperadminService.generateProtocolMediaThumbnails(chunk);
          const urls = (res.data && res.data.urls) || {};

          chunk.forEach(id => {
            const url = urls[id];
            if (url) {
              this.$set(this.resolvedThumbs, id, url);
            } else {
              this.markFailed(id);
            }
          });
        } catch (error) {
          // One failed chunk must not blank the rest of the grid.
          chunk.forEach(id => this.markFailed(id));
        } finally {
          this.resolvingIds = this.resolvingIds.filter(id => !chunk.includes(id));
        }
      }
    },
    thumbUrl(item) {
      return item.thumbnailURL || this.resolvedThumbs[item.id] || null;
    },
    isResolving(item) {
      return this.resolvingIds.includes(item.id);
    },
    markFailed(id) {
      if (!this.failedIds.includes(id)) this.failedIds.push(id);
    },
    openLightbox(index) {
      // The original is presigned by the listing but never requested until a
      // tile is opened — one image, on purpose, instead of one per tile.
      this.lightboxItems = this.items.map(item => ({
        id: item.id,
        src: item.originalURL,
        title: this.captionFor(item)
      }));
      this.lightboxIndex = index;
    },
    closeLightbox() {
      this.lightboxIndex = null;
      this.lightboxItems = [];
    },
    patientLabel(item) {
      const name = (item.patientName || "").trim();
      if (item.patientDn && name) return `${item.patientDn} · ${name}`;
      return item.patientDn || name || "—";
    },
    viewLabel(item) {
      const parts = [item.viewArch, item.viewSurface, item.viewSide].filter(Boolean);
      return parts.length ? parts.join(" / ") : item.protocolType || "—";
    },
    dateLabel(item) {
      const value = item.takenAt || item.creationDt;
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";
    },
    captionFor(item) {
      return `${item.clinicName || ""} ${this.patientLabel(item)} ${this.viewLabel(item)}`.trim();
    }
  }
};
</script>

<style scoped>
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.media-tile {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  transition: box-shadow 0.15s ease-in-out;
}

.media-tile:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.media-thumb {
  /* Fixed box so the grid does not reflow as lazily loaded tiles arrive. */
  height: 160px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.media-thumb-fallback {
  text-align: center;
}

.media-thumb-pending {
  text-align: center;
}

.media-caption {
  padding: 6px 8px;
  border-top: 1px solid #f1f3f5;
  font-size: 0.78rem;
  line-height: 1.3;
}
</style>
